import { Button, Popover } from '@nextui-org/react';
import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import RPNInputPrimitive from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import styled from 'styled-components';

import { CheckIcon } from '../../assets';
import ChevronIcon from '../../assets/Chevron';
import { ThemeProp } from '../../theme/Themes';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command/Command';
import Input, { InputProps } from '../Input';

type ConditionalProps =
  | { simple?: never; defaultCountry?: RPNInput.Country } // if simple is not passed, defaultCountry is optional
  | { simple: boolean; defaultCountry: RPNInput.Country }; // if simple is true, defaultCountry is required

export type PhoneInputProps = Omit<RPNInput.Props<typeof RPNInputPrimitive>, 'onChange' | 'defaultCountry'> & {
  onChange?: (value: RPNInput.Value) => void;
  error?: boolean | string;
} & ConditionalProps &
  Omit<InputProps, 'error' | 'onChange' | 'value' | 'ref'>;

const StyledPhoneInput = styled.div`
  display: flex;
`;

const StyledCountryButton = styled(Button)`
  display: flex;
  gap: 0.5rem;
  margin-right: 0.625rem;
  border-radius: 0.5rem;
  height: 2.625rem;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  padding: 0 0.75rem;
  z-index: 10;
  min-width: 0;
  width: 8rem;
`;

const StyledPopoverContent = styled(Popover.Content)`
  width: 300px;
  padding: 0;
  box-shadow: none;
`;

const StyledCommandContainer = styled.div`
  height: 15rem;
`;

const StyledFlagContainer = styled.span`
  display: flex;
  height: 1rem;
  width: 1.5rem;
  overflow: hidden;
  border-radius: 0.125rem;
  background: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};

  & svg {
    width: 100%;
    height: 100%;
  }
`;

const StyledCountryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledCountryName = styled.span`
  flex: 1;
  font-size: 0.875rem;
`;

const StyledCountryCode = styled.span`
  font-size: 0.875rem;
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
`;

const StyledCheckIcon = styled(CheckIcon)<{ $selected?: boolean }>`
  margin-left: auto;
  width: 1rem;
  height: 1rem;
  opacity: ${(props) => (props.$selected ? 1 : 0)};
`;

export const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange,
      limitMaxLength = true,
      smartCaret = true,
      simple,
      defaultCountry,
      onCountryChange,
      ...props
    },
    ref,
  ) => {
    const CountrySelectComponent = simple
      ? EmptyComponent
      : (p: CountrySelectProps) => <CountrySelect {...p} error={!!props.error} />;
    const [country, setCountry] = React.useState<RPNInput.Country | undefined>(defaultCountry);

    const handleCountryChange = (country: RPNInput.Country) => {
      setCountry(country);
      onCountryChange?.(country);
    };

    return (
      <StyledPhoneInput className={className}>
        <RPNInputPrimitive
          ref={ref}
          inputComponent={InputComponent}
          smartCaret={smartCaret}
          className="flex"
          limitMaxLength={limitMaxLength}
          countrySelectComponent={CountrySelectComponent}
          defaultCountry={country}
          onCountryChange={handleCountryChange}
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={(value) => onChange?.(value || ('' as RPNInput.Value))}
          {...props}
        />
      </StyledPhoneInput>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(({ ...props }, ref) => (
  <Input size="lg" aria-label={'auth.signUp.phoneNumber'} {...props} ref={ref} />
));
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
  error?: boolean;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  error,
}: CountrySelectProps) => {
  return (
    <Popover placement="bottom">
      <Popover.Trigger>
        <StyledCountryButton
          type="button"
          isDisabled={disabled}
          style={{
            borderColor: error ? 'var(--nextui-colors-error)' : undefined,
          }}
          className="flex h-full"
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          {selectedCountry ? `+${RPNInput.getCountryCallingCode(selectedCountry)}` : ''}
          <ChevronIcon
            style={{
              marginRight: '-0.5rem',
              width: '1rem',
              height: '1rem',
              opacity: disabled ? 0 : 0.5,
            }}
          />
        </StyledCountryButton>
      </Popover.Trigger>
      <StyledPopoverContent>
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <StyledCommandContainer>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <div key={`${value}-${label}`}>
                      <CountrySelectOption
                        country={value}
                        countryName={label}
                        selectedCountry={selectedCountry}
                        onChange={onChange}
                      />
                    </div>
                  ) : null,
                )}
              </CommandGroup>
            </StyledCommandContainer>
          </CommandList>
        </Command>
      </StyledPopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps {
  country: RPNInput.Country;
  countryName: string;
  selectedCountry?: RPNInput.Country;
  onChange?: (country: RPNInput.Country) => void;
}

const CountrySelectOption = ({ country, countryName, selectedCountry, onChange }: CountrySelectOptionProps) => {
  return (
    <CommandItem onSelect={() => onChange(country)}>
      <StyledCountryOption>
        <FlagComponent country={country} countryName={countryName} />
        <StyledCountryName>{countryName}</StyledCountryName>
        <StyledCountryCode>{`+${RPNInput.getCountryCallingCode(country)}`}</StyledCountryCode>
        <StyledCheckIcon $selected={country === selectedCountry} />
      </StyledCountryOption>
    </CommandItem>
  );
};

const EmptyComponent = () => null;

const FlagComponent = ({ country, countryName }: CountrySelectOptionProps) => {
  const Flag = flags[country];

  return <StyledFlagContainer>{Flag && <Flag title={countryName} />}</StyledFlagContainer>;
};
